import RtkQueryService from '@lok/services/RtkQueryService';

// IMPORTANT: do NOT use the project-wide transformErrorResponse here.
// That helper does `window.location.href = '/404'` on any 404 — fine for
// page-level loads, fatal for an inline tool dock. A super-admin clicking
// "Deactivate" on a user that was just deleted in another tab would yeet
// the whole SPA to /404. The agent dock has to surface errors inline.
const adminAgentTransformError = (response) => {
  const errorData = response?.data || response;
  return {
    status: response?.status,
    detail: errorData?.error || errorData?.detail || errorData?.message,
    raw: errorData,
    data: errorData
  };
};

// RTK Query slice for the new admin_agent Django app. All endpoints are
// gated server-side by IsSuperAdminOnly; the client guards mirror this at
// /super-admin/* and inside ChatDock.
const adminAgentService = RtkQueryService.injectEndpoints({
  endpoints: (builder) => ({
    // GET /v1/admin/agent/health/ — used at ChatDock boot so the UI can
    // show "agent online" before the user types anything.
    getAdminAgentHealth: builder.query({
      query: () => ({ url: '/v1/admin/agent/health/', method: 'GET' }),
      providesTags: ['AdminAgentHealth'],
      transformErrorResponse: adminAgentTransformError
    }),

    // GET /v1/admin/agent/tools/ — Claude-compatible tool catalog. The dock
    // uses this to render the right preview/result components per tool.
    getAdminAgentTools: builder.query({
      query: () => ({ url: '/v1/admin/agent/tools/', method: 'GET' }),
      providesTags: ['AdminAgentTools'],
      transformErrorResponse: adminAgentTransformError
    }),

    // The "where is Lori?" entry point — direct REST hit for the user table
    // and indirectly via the agent's semantic_search tool.
    searchAdminUsers: builder.query({
      query: ({ q = '', limit = 50, university } = {}) => ({
        url: '/v1/admin/users/search/',
        method: 'GET',
        params: {
          limit,
          ...(q ? { q } : {}),
          ...(university ? { university } : {})
        }
      }),
      providesTags: (result, error, arg) => [
        { type: 'AdminUserSearch', id: `${arg?.university || 'all'}:${arg?.q || 'all'}` }
      ],
      transformErrorResponse: adminAgentTransformError
    }),

    // POST /v1/admin/agent/tools/{name}/invoke/ — direct tool invocation.
    // Phase 0 supports semantic_search and list_recent_admin_actions; Phase 1
    // adds the preview/execute mutations.
    invokeAdminAgentTool: builder.mutation({
      query: ({ name, params = {} }) => ({
        url: `/v1/admin/agent/tools/${name}/invoke/`,
        method: 'POST',
        data: params
      }),
      // Executing a mutation invalidates the user search too: user.delete
      // and user.deactivate change what the Users table should show, and a
      // stale table after a confirmed delete reads as "it didn't work".
      // Invalidating the whole type refreshes every cached search variant,
      // whichever surface (confirm modal or chat dock) ran the action.
      invalidatesTags: (result, error, { name }) =>
        name === 'execute_action' ? ['AdminAuditLog', 'AdminUserSearch'] : [],
      transformErrorResponse: adminAgentTransformError
    }),

    // GET /v1/admin/agent/audit-log/ — backs the "recent actions" sidebar.
    listAdminAuditLog: builder.query({
      query: ({ limit = 25, actorId } = {}) => ({
        url: '/v1/admin/agent/audit-log/',
        method: 'GET',
        params: { limit, ...(actorId ? { actor_id: actorId } : {}) }
      }),
      providesTags: ['AdminAuditLog'],
      transformErrorResponse: adminAgentTransformError
    })
  })
});

export const {
  useGetAdminAgentHealthQuery,
  useGetAdminAgentToolsQuery,
  useSearchAdminUsersQuery,
  useLazySearchAdminUsersQuery,
  useInvokeAdminAgentToolMutation,
  useListAdminAuditLogQuery
} = adminAgentService;

export default adminAgentService;
